import { useEffect, useState } from "react";
import { ButtonGroup, Dropdown } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { FaCloudDownloadAlt, FaEye, FaFileExcel } from "react-icons/fa";
import { HiOutlineRectangleGroup } from "react-icons/hi2";
import { FaCar } from "react-icons/fa";
import { useTranslation } from "react-i18next";

import { Filters } from "../../components/Filters";
import Spinner from "../Components/Spinner";
import { ToastComponent } from "../Components/ToastComponent";
import GridReport from "../Components/Grid/GridReport";
import { useSelected } from "../../contexts/SelectedContext";
import { vehicleGroupService } from "../../Services/vehicle.group.service";
import { vehicleService } from "../../Services/vehicle.service";
import { summaryVehiclesService } from "../../Services/summary.vehicles.service";
import { downloadService } from "../../Services/download.service";

const PAGE_GRID = "gridSummaryVehicles";
const PAGE_TITLE = "Resumo de Veiculos";

const getSelectedValue = (value, fallback = "0") => {
  if (Array.isArray(value)) return value[0] ?? fallback;
  return value ?? fallback;
};

const normalizeVehicleId = (value) => {
  if (value === "TODOS") return 0;
  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? value : numericValue;
};

export default function SummaryVehicles() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { selectedValues, setSelectedValues } = useSelected();

  const [vehicleGroups, setVehicleGroups] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [gridInfo, setGridInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [toastParams, setToastParams] = useState("");

  const selectedGroup = getSelectedValue(selectedValues.veiculos, "0");
  const selectedVehicle = normalizeVehicleId(
    getSelectedValue(selectedValues.veiculosGroups, "TODOS")
  );

  useEffect(() => {
    dispatch({ type: "title", value: PAGE_TITLE });
  }, [dispatch]);

  useEffect(() => {
    fetchVehicleGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchVehiclesByGroup(selectedGroup);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup]);

  const fetchVehicleGroups = async () => {
    try {
      setFiltersLoading(true);
      const groups = await vehicleGroupService.fetchGroups();
      const safeGroups = Array.isArray(groups) ? groups : [];
      setVehicleGroups(safeGroups);

      if (safeGroups.length === 1) {
        setSelectedValues((prev) => ({
          ...prev,
          veiculos: [safeGroups[0].vehicleGroupId],
        }));
      } else {
        setSelectedValues((prev) => ({
          ...prev,
          veiculos: [0],
        }));
      }
    } catch (error) {
      console.log(error);
      setVehicleGroups([]);
    } finally {
      setFiltersLoading(false);
    }
  };

  const fetchVehiclesByGroup = async (groupId) => {
    try {
      setFiltersLoading(true);
      const vehiclesResponse = await vehicleService.fetchVehicles(groupId);
      const safeVehicles = Array.isArray(vehiclesResponse) ? vehiclesResponse : [];
      setVehicles(safeVehicles);
      setSelectedValues((prev) => ({
        ...prev,
        veiculosGroups: ["TODOS"],
      }));
    } catch (error) {
      console.log(error);
      setVehicles([]);
    } finally {
      setFiltersLoading(false);
    }
  };

  const buildPayload = () => ({
    groupId: selectedGroup,
    vehicleId: selectedVehicle,
    splitByPlateSheets: false,
  });

  const showToast = (body, color = "Warning", title = t("REPORTS.REPORT_REQUEST")) => {
    setToastParams(
      JSON.stringify({
        show: true,
        title,
        body,
        color,
      })
    );
  };

  // Chamado pelo GridReport somente quando a montagem do grid termina —
  // só então o spinner é encerrado e se valida se a resposta veio vazia.
  const handleGridRendered = () => {
    setIsLoading(false);
  };

  const handleDisplay = async () => {
    try {
      setIsLoading(true);
      const response = await summaryVehiclesService.fetchSummaryVehicles(buildPayload());
      setGridInfo(response ?? []);
    } catch (error) {
      console.error(error);
      showToast("Erro ao buscar resumo de veiculos.", "Danger", "Erro");
      setIsLoading(false);
    }
  };

  const handleDownload = async (fileType) => {
    try {
      setIsLoading(true);
      showToast("Processando download do relatorio...");

      const serviceMethod =
        fileType === "pdf"
          ? summaryVehiclesService.fetchSummaryVehiclesPdf
          : summaryVehiclesService.fetchSummaryVehiclesXls;

      const response = await serviceMethod(buildPayload());
      if (!response) {
        throw new Error("Arquivo nao retornado pela API.");
      }

      const downloaded = downloadService.downloadApiFile(
        response,
        fileType,
        PAGE_TITLE
      );

      if (!downloaded) {
        throw new Error("Nao foi possivel baixar o arquivo retornado pela API.");
      }

      showToast("Download realizado com sucesso.", "Success");
    } catch (error) {
      console.error(error);
      showToast("Falha ao gerar relatorio.", "Danger", "Erro");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container-xxl flex-grow-1 container-p-y">
      <ToastComponent
        params={toastParams}
        closeToast={() => setToastParams(JSON.stringify({ show: false }))}
      />

      <div className="row mb-3">
        <div className="col-md-4 col-12 mb-2 mb-md-0">
          <Filters
            id="veiculos"
            isMulti={false}
            title={t("GROUPS.SELECT_GROUP")}
            hasSubTitle={false}
            options={vehicleGroups}
            icon={HiOutlineRectangleGroup}
            hasIcon={true}
            pageTitle={PAGE_GRID}
            isLoading={filtersLoading}
          />
        </div>

        <div className="col-md-4 col-12 mb-2 mb-md-0">
          <Filters
            id="veiculosGroups"
            isMulti={false}
            title={t("FILTERS_COMPONENT.VEHICLE")}
            hasSubTitle={true}
            subTitle={t("FILTERS_COMPONENT.ALL_VEHICLES")}
            options={vehicles}
            icon={FaCar}
            hasIcon={true}
            pageTitle={PAGE_GRID}
            isLoading={filtersLoading}
          />
        </div>

        <div className="col-md-2 col-12 text-md-end text-center">
          <Dropdown as={ButtonGroup}>
            <Dropdown.Toggle
              id="dropdown-custom-button"
              variant="primary"
              className="w-100 w-md-auto"
              disabled={isLoading}
            >
              <FaCloudDownloadAlt className="icon-in-button" />{" "}
              {t("FILTERS_COMPONENT.REPORTS")}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={handleDisplay}>
                <FaEye className="me-2" /> {t("FILTERS_COMPONENT.DISPLAY")}
              </Dropdown.Item>
              <Dropdown.Item onClick={() => handleDownload("pdf")}>
                <FaEye className="me-2" /> {t("FILTERS_COMPONENT.PDF")}
              </Dropdown.Item>
              <Dropdown.Item onClick={() => handleDownload("xls")}>
                <FaFileExcel className="me-2" /> XLS
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      {gridInfo !== null && (
        // Mantém montado durante o loading para o Syncfusion terminar de
        // montar/paginar (dataBound) antes de revelar. `visibility:hidden`
        // preserva as dimensões do container — `display:none` zera a
        // largura/altura e faz o Grid renderizar como se estivesse vazio.
        <div
          style={
            isLoading
              ? { position: "absolute", visibility: "hidden", width: "100%", pointerEvents: "none" }
              : undefined
          }
        >
          <GridReport
            gridData={gridInfo}
            pageGrid={PAGE_GRID}
            onGridRendered={handleGridRendered}
          />
        </div>
      )}
      {isLoading && <Spinner />}
    </div>
  );
}
